import { NodeIndexOutlined, UserOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DeviceGroupsIcon, DevicesIcon, TeamIcon, UserIcon } from 'assets/images/svg';
import { constants } from 'config/constants';
import { buildDeviceOptionsWithIntegration, buildGroupOptionsWithType } from 'library/helpers/integrationHelpers';
import { formatDisplayName } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectTeams } from 'reduxStore/teams/selectors';
import { permissionsActions } from 'reduxStore/permissions/slice';
import { Button, Col, Flex, Form, Modal, Row, Select, Steps, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const GrantPermissionsModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	// ✅ Hook
	const {
		token: { colorTextLabel, fontWeightStrong, fontSizeLG, fontSizeSM, paddingXL, colorPrimary, marginXL }
	} = theme.useToken();
	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const submitRef = useRef(false);

	// ✅ Redux State
	const people = useSelector(selectPeople);
	const teams = useSelector(selectTeams);
	const devices = useSelector(selectDevices);
	const deviceGroups = useSelector(selectAssetGroups);
	const { loadingAddPermission, error: grantPermissionError } = useSelector((state) => state.Permissions);

	// ✅ Local State
	const [currentStep, setCurrentStep] = useState(0);
	const [internalVisible, setInternalVisible] = useState(false);
	const [selectedPeople, setSelectedPeople] = useState([]);
	const [selectedTeams, setSelectedTeams] = useState([]);
	const [selectedDevices, setSelectedDevices] = useState([]);
	const [selectedDeviceGroups, setSelectedDeviceGroups] = useState([]);

	// ✅ Handlers
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Permissions
	const canManagePermissions = useManagePermittedRoles('aCPermissionManagement');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	// ✅ Handler to close the modal
	const handleCancel = () => {
		setVisible(false);

		if (loadingAddPermission) {
			return;
		}

		setCurrentStep(0);
		form.resetFields();
		setSelectedPeople([]);
		setSelectedTeams([]);
		setSelectedDevices([]);
		setSelectedDeviceGroups([]);

		submitRef.current = false;
	};

	// ✅ Handler to clear all selected tags
	const handleClear = () => {
		if (loadingAddPermission) {
			return;
		}

		setSelectedPeople([]);
		setSelectedTeams([]);
		setSelectedDevices([]);
		setSelectedDeviceGroups([]);
	};

	// ✅ Handler to go to the next step
	const handleNext = async () => {
		try {
			if (currentStep === 0 && selectedPeople.length === 0 && selectedTeams.length === 0) return;
			if (currentStep === 1 && selectedDevices.length === 0 && selectedDeviceGroups.length === 0) return;
			setCurrentStep((prev) => prev + 1);
		} catch (_) {}
	};

	// ✅ Handler to grant permissions
	const handleSubmit = async () => {
		const payload = {
			...(selectedDevices.length > 0 && { deviceIDs: selectedDevices }),
			...(selectedPeople.length > 0 && { personIDs: selectedPeople }),
			...(selectedDeviceGroups.length > 0 && { assetGroupIDs: selectedDeviceGroups }),
			...(selectedTeams.length > 0 && { teamIDs: selectedTeams }),
			permissionTypeID: 1
		};

		dispatch(permissionsActions.grantPermission(payload));
		submitRef.current = true;
	};
	useEffect(() => {
		if (!submitRef.current) return;

		// still submitting? do nothing
		if (loadingAddPermission) return;

		// done submitting
		if (!loadingAddPermission && !grantPermissionError) {
			// ✅ success! now tear everything down
			setVisible(false);
			setCurrentStep(0);
			form.resetFields();
			setSelectedPeople([]);
			setSelectedTeams([]);
			setSelectedDevices([]);
			setSelectedDeviceGroups([]);
		}

		submitRef.current = false;
	}, [loadingAddPermission, form, grantPermissionError, setVisible]);

	// ✅ Options for Select components
	const peopleOptions = people.map((p) => ({ label: formatDisplayName(p)?.displayName, value: p.personID }));
	const teamOptions = teams.map((t) => ({ label: t.name, value: t.teamID }));
	const deviceOptions = buildDeviceOptionsWithIntegration(
		devices.filter((d) => !constants.GATEWAY_CATEGORIES.includes(d.defaultCategoryID))
	);
	const deviceGroupOptions = buildGroupOptionsWithType(deviceGroups);

	// ✅ Steps data
	const stepsData = [
		{ key: 0, label: 'People & Teams', icon: <UserOutlined /> },
		{ key: 1, label: 'Devices & Groups', icon: <NodeIndexOutlined /> }
	];

	const stepsItems = stepsData.map((step) => ({
		title: (
			<Text
				style={{
					color: currentStep === step.key ? colorPrimary : undefined,
					fontSize: fontSizeLG,
					cursor: 'pointer'
				}}
				onClick={() => {
					if (step.key <= currentStep) setCurrentStep(step.key);
				}}
			>
				{step.label}
			</Text>
		),
		icon: step.icon,
		status: currentStep === step.key ? 'process' : 'wait',
		style: {
			height: 70
		}
	}));

	const getTagColorAndIcon = (label) => {
		switch (label.toLowerCase()) {
			case 'people':
				return {
					color: 'purple',
					icon: <UserIcon style={{ marginRight: 4 }} color='currentColor' />
				};
			case 'teams':
				return { color: 'cyan', icon: <TeamIcon style={{ marginRight: 4 }} color='currentColor' /> };
			case 'devices':
				return { color: 'gold', icon: <DevicesIcon style={{ marginRight: 4 }} color='currentColor' /> };
			case 'device groups':
				return { color: 'blue', icon: <DeviceGroupsIcon style={{ marginRight: 4 }} color='currentColor' /> };
			default:
				return { color: 'default', icon: null };
		}
	};

	const renderSelectWithTags = (label, placeholder, options, selected, setSelected) => {
		const { color, icon } = getTagColorAndIcon(label);

		return (
			<div>
				<Text style={{ fontWeight: fontWeightStrong }}>{label}</Text>
				<Select
					style={{ marginTop: 10, width: '100%' }}
					showSearch
					placeholder={placeholder}
					options={options}
					value={null}
					onSelect={(value) => {
						if (!selected.includes(value)) setSelected([...selected, value]);
					}}
					filterOption={(input, option) =>
						(option?.searchLabel || option?.label || '').toString().toLowerCase().includes(input.toLowerCase())
					}
				/>
				<Flex wrap='wrap' gap='small' style={{ marginTop: 16 }}>
					{selected.map((id) => (
						<Tag
							key={id}
							color={color}
							closable
							style={{
								fontSize: fontSizeSM,
								fontWeight: fontWeightStrong,
								display: 'flex',
								alignItems: 'center',
								borderColor: 'currentcolor'
							}}
							onClose={() => setSelected(selected.filter((item) => item !== id))}
						>
							{icon}
							{options.find((opt) => opt.value === id)?.displayLabel ||
								options.find((opt) => opt.value === id)?.label}
						</Tag>
					))}
				</Flex>
			</div>
		);
	};

	return (
		<>
			<Tooltip title={!canManagePermissions ? permissionDeniedMessage : 'Manage Permission'} placement='top'>
				<span onClick={(e) => canManagePermissions && setVisible(true)}>
					{children ?? (
						<Button type='primary' disabled={!canManagePermissions}>
							Manage Permissions
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				closable={false}
				onCancel={handleCancel}
				width={930}
				centered
				styles={{ body: { height: 565 }, content: { padding: paddingXL } }}
				footer={[
					<Flex justify='flex-end' gap={8} key='footer'>
						<Button onClick={handleClear} disabled={loadingAddPermission}>
							Clear
						</Button>
						<Flex gap={8}>
							{currentStep < stepsData.length - 1 ? (
								<Button
									htmlType='button'
									type='primary'
									onClick={handleNext}
									loading={loadingAddPermission}
									disabled={loadingAddPermission}
								>
									Next
								</Button>
							) : (
								<Button
									htmlType='button'
									type='primary'
									onClick={handleSubmit}
									loading={loadingAddPermission}
								>
									Grant Permission
								</Button>
							)}
						</Flex>
					</Flex>
				]}
			>
				<Flex vertical style={{ marginBottom: marginXL }}>
					<Title level={4} style={{ margin: 0, lineHeight: '28px' }}>
						Manage Permissions
					</Title>
					<Text style={{ color: colorTextLabel, lineHeight: '20px' }}>
						Select a person or team and a device or device group to assign an access permission.
					</Text>
				</Flex>

				<Form form={form} layout='vertical' initialValues={{}}>
					<Row>
						<Col span={8}>
							<Steps
								direction='vertical'
								current={currentStep}
								style={{ height: 100 }}
								items={stepsItems}
							/>
						</Col>

						<Col span={16}>
							{currentStep === 0 && (
								<Flex gap={26} vertical>
									{renderSelectWithTags(
										'People',
										'Enter person name or email',
										peopleOptions,
										selectedPeople,
										setSelectedPeople
									)}
									{renderSelectWithTags(
										'Teams',
										'Enter team name',
										teamOptions,
										selectedTeams,
										setSelectedTeams
									)}
								</Flex>
							)}

							{currentStep === 1 && (
								<Flex gap={26} vertical>
									{renderSelectWithTags(
										'Devices',
										'Enter device name',
										deviceOptions,
										selectedDevices,
										setSelectedDevices
									)}
									{renderSelectWithTags(
										'Device Groups',
										'Enter device group name',
										deviceGroupOptions,
										selectedDeviceGroups,
										setSelectedDeviceGroups
									)}
								</Flex>
							)}

							{currentStep === 2 && <Text type='secondary'>Duration step content coming soon...</Text>}
						</Col>
					</Row>
				</Form>
			</Modal>
		</>
	);
};

export default GrantPermissionsModal;
